import React from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { formatCurrencyWithSymbol } from '../utils/formatters';
import { getDivisionConfig, getDivisionLabel } from '../../../config/divisions';
import { getReasonLabel } from '../../../config/deal-status-reasons-master';
import { UserIcon } from '@heroicons/react/24/outline';
import DealTemperature from './DealTemperature';

const parsePrimaryProductLine = (notes) => {
  const text = String(notes || '');
  const firstLine = text.split('\n').find((line) => /^\s*1\.\s*Product:/i.test(line));
  if (!firstLine) return null;

  const readValue = (label) => {
    const match = firstLine.match(new RegExp(`${label}:([^|\\n]+)`, 'i'));
    return match?.[1]?.trim() || '';
  };

  return {
    product: readValue('Product'),
    grade: readValue('Grade'),
    size: readValue('Size'),
    qty: readValue('Qty'),
    length: readValue('Length'),
  };
};

const formatClosureDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatOwnerName = (value) => {
  const text = String(value || '').trim();
  if (!text || text === 'Removed User') return 'Inactive User';
  return text;
};

const hasMeaningfulValue = (value) => Number(value || 0) > 0;

// Deal Card Component
const DealCard = ({ deal, index, onCardClick, dealStatus = null, onEditDealStatus = () => {}, onClearDeal = () => {}, onEditDeal = () => {}, onTemperatureChange = () => {} }) => {
  const divisionConfig = getDivisionConfig(deal.Division);
  const isPendingApproval = deal.Status === 'PENDING';
  const isRejected = deal.Status === 'REJECTED';
  const isLost = deal.Stage === 'DEAL_LOST';
  const primaryLine = parsePrimaryProductLine(deal.Notes || deal.notes);
  const followUpDate = formatClosureDate(deal.ExpectedClosureDate);
  const enquiryDate = formatClosureDate(deal.EnquiryDate || deal.enquiry_date);
  const leadSource = String(deal.LeadSource || '').trim();
  const ownerName = formatOwnerName(deal.SalespersonName);
  const salesManager = deal.SMName || deal.sm_name || deal.KAMECode || deal.kam_ecode || '';
  const hasTemperature = Boolean(deal.Temperature);
  const notePreview = String(deal.WonNotes || deal.won_notes || '').trim();
  const primaryItem = Array.isArray(deal.items) && deal.items.length > 0 ? deal.items[0] : null;
  const items = Array.isArray(deal.items) ? deal.items : [];
  const inferredPriceUom = () => {
    if (primaryItem?.price_uom || primaryItem?.PriceUom) return primaryItem.price_uom || primaryItem.PriceUom;
    if (deal?.price_uom || deal?.PriceUom) return deal.price_uom || deal.PriceUom;
    const notesText = String(deal.Notes || deal.notes || '');
    const match = notesText.match(/\b(EUR|USD|GBP)\b/i);
    return match ? match[1].toUpperCase() : '';
  };
  const priceUom = inferredPriceUom();

  const computeDealValueFromItems = () => {
    if (!items.length) return 0;
    return items.reduce((sum, item) => {
      const qty = Number.parseFloat(item?.qty ?? 0) || 0;
      const price = Number.parseFloat(item?.price_offer ?? 0) || 0;
      const uom = String(item?.uom || '').toLowerCase();
      const normalizedQty = ['ton', 'tons', 'mt'].includes(uom) ? qty * 1000 : qty;
      return sum + normalizedQty * price;
    }, 0);
  };

  const computedDealValue = computeDealValueFromItems();
  const displayDealValue = hasMeaningfulValue(deal.DealValue) ? deal.DealValue : computedDealValue;
  
  // Use backend-provided Draggable property instead of local logic
  const isDragDisabled = !deal.Draggable;

  const handleClick = (e) => {
    // Prevent click during drag
    if (e.defaultPrevented) return;
    onCardClick?.(deal);
  };

  const handleEditStatus = (e) => {
    e.stopPropagation(); // Prevent card click
    onEditDealStatus(deal);
  };

  const handleClearDeal = (e) => {
    e.stopPropagation(); // Prevent card click
    onClearDeal(deal);
  };

  const handleEditDeal = (e) => {
    e.stopPropagation(); // Prevent card click
    onEditDeal(deal);
  };

  const showStatus = dealStatus && (deal.Stage === 'DEAL_ON_HOLD' || deal.Stage === 'DEAL_LOST');

  const handleSendToPMS = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Send "${deal.AccountName}" to PMS for production?`)) return;
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`/api/v1/deals/${deal.ID}/queue-pms`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        alert('✅ Sent to PMS! SO will be created within 60 seconds.');
      } else {
        alert(`❌ ${data.detail || 'Failed to send to PMS'}`);
      }
    } catch (err) {
      alert('❌ Error connecting to server');
    }
  };

  return (
    <Draggable 
      draggableId={`deal-${deal.ID}`} 
      index={index}
      isDragDisabled={isDragDisabled}
    >
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={handleClick}
          className={`
            p-3 mb-2 bg-white rounded-lg shadow-sm border-2 transition-all duration-150 
            ${isPendingApproval 
              ? 'cursor-pointer border-orange-200 bg-orange-50' 
              : isRejected 
                ? 'cursor-pointer border-red-200 bg-red-50'
                : isLost
                  ? 'cursor-pointer border-gray-200 bg-gray-50'
                  : deal.Draggable
                    ? 'cursor-grab active:cursor-grabbing hover:cursor-pointer'
                    : 'cursor-pointer'
            }
            ${!isPendingApproval && !isRejected && !isLost && divisionConfig.borderColor}
            ${snapshot.isDragging ? 
              `shadow-lg ring-2 ${divisionConfig.ringColor} ring-opacity-50 transform rotate-1 scale-105` : 
              'hover:shadow-md'
            }
            ${(isPendingApproval || isRejected || isLost || !deal.Draggable) ? 'opacity-90' : ''}
          `}
          style={{
            ...provided.draggableProps.style,
            transform: snapshot.isDragging 
              ? `${provided.draggableProps.style?.transform} rotate(1deg) scale(1.05)`
              : provided.draggableProps.style?.transform
          }}
        >
          {/* Approval Status Banner */}
          {isPendingApproval && (
            <div className="mb-2 -mx-3 -mt-3 px-3 py-2 bg-orange-100 border-b border-orange-200">
              <div className="flex items-center text-xs text-orange-700">
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">
                  {deal.Flag === 'KAM_APPROVAL' ? 'Pending SM Approval' : 
                   deal.Flag === 'DUPLICATE' ? 'Pending MD Office Approval' : 
                   'Pending Approval'}
                </span>
              </div>
            </div>
          )}

          {/* Rejected Status Banner */}
          {isRejected && (
            <div className="mb-2 -mx-3 -mt-3 px-3 py-2 bg-red-100 border-b border-red-200">
              <div className="flex items-center justify-between text-xs text-red-700">
                <div className="flex items-center">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span className="font-medium">Rejected</span>
                </div>
                <button
                  onClick={handleClearDeal}
                  className="text-red-600 hover:text-red-800 font-medium"
                  title="Clear this deal from view"
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          {/* Lost Deal Banner */}
          {isLost && !isRejected && (
            <div className="mb-2 -mx-3 -mt-3 px-3 py-2 bg-gray-100 border-b border-gray-200">
              <div className="flex items-center justify-between text-xs text-gray-700">
                <div className="flex items-center">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                  </svg>
                  <span className="font-medium">Deal Lost</span>
                </div>
                {/* <button
                  onClick={handleClearDeal}
                  className="text-gray-600 hover:text-gray-800 font-medium"
                  title="Clear this deal from view"
                >
                  Clear
                </button> */}
              </div>
            </div>
          )}

          {/* Company and Service Type */}
          <div className="mb-2">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-semibold text-gray-900 text-sm leading-tight line-clamp-2" title={deal.AccountName}>
                {deal.AccountName}
                {(deal.EnquiryID || deal.enquiry_id) && (
                  <span className="ml-1 text-xs font-normal text-gray-400">#{deal.EnquiryID || deal.enquiry_id}</span>
                )}
              </h3>
            </div>
            <p className="text-xs text-gray-600 truncate leading-tight mt-1" title={deal.ServiceType}>
              {deal.ServiceType}
            </p>
          {primaryLine && (
            <div className="mt-2 grid grid-cols-2 gap-1 text-[11px] text-gray-600">
                <span className="truncate" title={primaryLine.product || '-'}>
                  Product: <span className="font-medium text-gray-800">{primaryLine.product || '-'}</span>
                </span>
                <span className="truncate" title={primaryLine.grade || '-'}>
                  Grade: <span className="font-medium text-gray-800">{primaryLine.grade || '-'}</span>
                </span>
                <span className="truncate" title={primaryLine.size || '-'}>
                  Size: <span className="font-medium text-gray-800">{primaryLine.size || '-'}</span>
                </span>
                <span className="truncate" title={primaryLine.qty || '-'}>
                  Qty: <span className="font-medium text-gray-800">{primaryLine.qty || '-'} {primaryLine.uom || ''}</span>
                </span>
                <span className="truncate col-span-2" title={primaryLine.length || '-'}>
                  Length: <span className="font-medium text-gray-800">{primaryLine.length || '-'}</span>
                </span>
              </div>
            )}
          </div>

          {/* Deal Value with Edit Icon */}
          {(hasMeaningfulValue(displayDealValue) || deal.Draggable) && (
            <div className="mb-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Value</span>
                <div className="flex items-center gap-1">
                  {hasMeaningfulValue(displayDealValue) ? (
                    <span className="text-sm font-semibold text-green-600">
                      {formatCurrencyWithSymbol(displayDealValue, priceUom)}
                    </span>
                  ) : (
                    <span className="text-xs font-medium text-gray-400">Add value</span>
                  )}
                  {deal.Draggable && (
                    <button
                      onClick={handleEditDeal}
                      className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                      title="Edit deal"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Division Badge */}
          <div className="mb-2">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${divisionConfig.badgeColor}`}>
              {getDivisionLabel(deal.Division)}
            </span>
          </div>

          {(enquiryDate || leadSource) && (
            <div className={`grid gap-2 text-[11px] text-gray-600 ${enquiryDate && leadSource ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {enquiryDate && (
                <div className="rounded-md bg-gray-50 px-2 py-1">
                  <div className="text-[10px] uppercase tracking-wide text-gray-400">Enquiry Date</div>
                  <div className="font-medium text-gray-800">{enquiryDate}</div>
                </div>
              )}
              {leadSource && (
                <div className="rounded-md bg-gray-50 px-2 py-1">
                  <div className="text-[10px] uppercase tracking-wide text-gray-400">Source</div>
                  <div className="font-medium text-gray-800 truncate" title={leadSource}>
                    {leadSource}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Deal Temperature - show in Offer stage */}
          {deal.Stage === 'OFFER_SUBMITTED' && hasTemperature && (
            <div className="mb-2">
              <DealTemperature
                deal={deal}
                isEditable={deal.Draggable}
                onTemperatureChange={onTemperatureChange}
                compact={true}
              />
            </div>
          )}

          {/* Deal Status (if applicable) */}
          {showStatus && dealStatus && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <span className="text-xs text-gray-500">Reason:</span>
                  <p className="text-xs text-gray-700 font-medium truncate" title={getReasonLabel(dealStatus.Status, dealStatus.Reason)}>
                    {getReasonLabel(dealStatus.Status, dealStatus.Reason)}
                  </p>
                </div>
                {deal.Draggable && (
                  <button
                    onClick={handleEditStatus}
                    className="ml-2 p-1 text-gray-400 hover:text-blue-600 transition-colors"
                    title="Edit status reason"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Send to PMS Button - only for ORDER_WON */}
          {deal.Stage === 'ORDER_WON' || deal.Stage === 'DEAL_WON' && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <button
                onClick={handleSendToPMS}
                className="w-full py-1.5 px-3 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-md transition-colors flex items-center justify-center gap-1"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Send to PMS
              </button>
            </div>
          )}

          {/* Contact Person */}
          {deal.ContactPerson && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <div className="flex items-center text-xs text-gray-500">
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="truncate" title={deal.ContactPerson}>
                  {deal.ContactPerson}
                </span>
              </div>
              {deal.ContactPhone && (
                <div className="mt-1 text-[11px] text-gray-500 truncate" title={deal.ContactPhone}>
                  {deal.ContactPhone}
                </div>
              )}
            </div>
          )}

          {/* Salesperson */}
          {ownerName && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <div className="flex items-center text-xs text-gray-500">
                <UserIcon className="w-3 h-3 mr-1" />
                <span className="truncate" title={ownerName}>
                  {ownerName}
                </span>
              </div>
            </div>
          )}
          {salesManager && (
            <div className="mt-1">
              <div className="flex items-center text-[11px] text-gray-500">
                <span className="mr-1 font-semibold text-gray-400">SM</span>
                <span className="truncate" title={salesManager}>
                  {salesManager}
                </span>
              </div>
            </div>
          )}
          {notePreview && (
            <div className="mt-2 rounded-md bg-gray-50 px-2 py-1">
              <div className="text-[10px] uppercase tracking-wide text-gray-400">Note</div>
              <p className="text-[11px] text-gray-700 line-clamp-2 whitespace-pre-wrap">{notePreview}</p>
            </div>
          )}
        </div>
      )}
    </Draggable>
  );
};

export default DealCard;
